import Swiper from 'swiper/bundle';

class ProductGallery {
  constructor(sectionSelector = '.product-gallery') {
    this.activeClass = 'product-gallery__slide--active';
    this.enteringClass = 'is-entering';
    this.changingClass = 'is-changing';

    document.querySelectorAll(sectionSelector).forEach(section => {
      this.setup(section);
    });
  }

  setup(section) {
    const manager = {
      section,

      viewport: section.querySelector(
        '.product-gallery__viewport'
      ),

      track: section.querySelector(
        '.product-gallery__track'
      ),

      description: section.querySelector(
        '.product-gallery__description'
      ),

      current: section.querySelector(
        '.product-gallery__counter-current'
      ),

      total: section.querySelector(
        '.product-gallery__counter-total'
      ),

      prev: section.querySelector(
        '.product-gallery__arrow--prev'
      ),

      next: section.querySelector(
        '.product-gallery__arrow--next'
      ),

      slidesCount: 0,
      initialIndex: 0,
      swiper: null,
    };

    if (!manager.viewport || !manager.track) return;

    this.prepareSlides(manager);
    this.initSwiper(manager);
    this.bindNavigation(manager);
  }

  prepareSlides(manager) {
    const slides = [
      ...manager.track.querySelectorAll(
        '.product-gallery__slide'
      ),
    ];

    manager.slidesCount = slides.length;
    manager.initialIndex = manager.slidesCount;

    if (!manager.slidesCount) return;

    if (manager.total) {
      manager.total.textContent = manager.slidesCount;
    }

    slides.forEach((slide, index) => {
      slide.classList.remove(
        this.activeClass,
        this.enteringClass
      );

      slide.dataset.realIndex = index;
    });

    this.initMagnificPopup(manager, slides);

    const before = slides.map(slide => {
      const clone = slide.cloneNode(true);

      clone.classList.remove(
        this.activeClass,
        this.enteringClass
      );

      return clone;
    });

    const after = slides.map(slide => {
      const clone = slide.cloneNode(true);

      clone.classList.remove(
        this.activeClass,
        this.enteringClass
      );

      return clone;
    });

    manager.track.prepend(...before);
    manager.track.append(...after);

    const preparedSlides = [
      ...manager.track.querySelectorAll(
        '.product-gallery__slide'
      ),
    ];

    preparedSlides[manager.initialIndex]
      ?.classList.add(this.activeClass);
  }

  initSwiper(manager) {
    manager.swiper = new Swiper(manager.viewport, {
      initialSlide: manager.initialIndex,

      loop: false,
      speed: 450,

      spaceBetween: 8,
      slidesPerView: 'auto',
      centeredSlides: true,

      watchSlidesProgress: true,
      slideToClickedSlide: false,

      breakpoints: {
        0: {
          slidesPerView: 1,
          centeredSlides: false,
          spaceBetween: 0,
        },

        768: {
          slidesPerView: 'auto',
          centeredSlides: true,
          spaceBetween: 8,
        },

        1024: {
          slidesPerView: 'auto',
          centeredSlides: true,
          spaceBetween: 8,
        },
      },

      on: {
        init: swiper => {
          this.updateUI(
            manager,
            swiper.slides[swiper.activeIndex]
          );
        },

        sliderMove: () => {
          this.hideUI(manager);
        },

        slideChangeTransitionStart: swiper => {
          this.onTransitionStart(
            manager,
            swiper
          );
        },

        transitionEnd: swiper => {
          this.onTransitionEnd(
            manager,
            swiper
          );
        },

        click: swiper => {
          this.onSlideClick(
            manager,
            swiper
          );
        },

        resize: swiper => {
          this.onResize(
            manager,
            swiper
          );
        },
      },
    });
  }

  bindNavigation(manager) {
    manager.prev?.addEventListener('click', () => {
      this.goTo(
        manager,
        manager.swiper.activeIndex - 1
      );
    });

    manager.next?.addEventListener('click', () => {
      this.goTo(
        manager,
        manager.swiper.activeIndex + 1
      );
    });
  }

  goTo(manager, index) {
    const swiper = manager.swiper;

    if (
      !swiper ||
      swiper.animating ||
      !swiper.slides[index]
    ) {
      return;
    }

    this.hideUI(manager);

    this.setActive(
      manager,
      index,
      true
    );

    requestAnimationFrame(() => {
      swiper.slideTo(
        index,
        swiper.params.speed,
        true
      );
    });
  }

  setActive(manager, index, animate = false) {
    const swiper = manager.swiper;
    const slide = swiper.slides[index];

    if (!slide) return;

    swiper.slides.forEach(item => {
      item.classList.remove(
        this.activeClass,
        this.enteringClass
      );
    });

    slide.classList.add(this.activeClass);

    this.syncActiveLink(manager, slide);

    if (animate) {
      slide.classList.add(
        this.enteringClass
      );
    }

    swiper.updateSlides();
  }

  onTransitionStart(manager, swiper) {
    this.hideUI(manager);

    const activeSlide =
      swiper.slides[swiper.activeIndex];

    if (!activeSlide) return;

    if (
      !activeSlide.classList.contains(
        this.activeClass
      )
    ) {
      this.setActive(
        manager,
        swiper.activeIndex,
        true
      );
    }
  }

  onTransitionEnd(manager, swiper) {
    const slide = this.normalizePosition(
      manager,
      swiper
    );

    swiper.slides.forEach(item => {
      item.classList.remove(
        this.enteringClass
      );
    });

    this.updateUI(manager, slide);
  }

  normalizePosition(manager, swiper) {
    const activeSlide =
      swiper.slides[swiper.activeIndex];

    if (!activeSlide) return null;

    const realIndex = Number(
      activeSlide.dataset.realIndex
    );

    const normalizedIndex =
      manager.slidesCount + realIndex;

    if (
      swiper.activeIndex === normalizedIndex
    ) {
      return activeSlide;
    }

    swiper.slides.forEach(slide => {
      slide.classList.remove(
        this.activeClass,
        this.enteringClass
      );
    });

    const normalizedSlide =
      swiper.slides[normalizedIndex];

    if (!normalizedSlide) {
      return activeSlide;
    }

    normalizedSlide.classList.add(
      this.activeClass
    );

    swiper.updateSlides();

    swiper.slideTo(
      normalizedIndex,
      0,
      false
    );

    return normalizedSlide;
  }

  onSlideClick(manager, swiper) {
    if (window.innerWidth < 768) return;

    if (
      swiper.clickedSlide?.querySelector(
        '.product-gallery__link'
      )
    ) {
      return;
    }

    const index = swiper.clickedIndex;

    if (
      index == null ||
      index === swiper.activeIndex
    ) {
      return;
    }

    this.goTo(manager, index);
  }

  onResize(manager, swiper) {
    requestAnimationFrame(() => {
      swiper.updateSize();
      swiper.updateSlides();

      const slide = this.normalizePosition(
        manager,
        swiper
      );

      swiper.slideTo(
        swiper.activeIndex,
        0,
        false
      );

      this.updateUI(manager, slide);
    });
  }

  hideUI(manager) {
    manager.description?.classList.add(
      this.changingClass
    );

    manager.current?.classList.add(
      this.changingClass
    );
  }

  updateUI(manager, slide) {
    if (!slide) return;

    this.syncActiveLink(manager, slide);

    const realIndex = Number(
      slide.dataset.realIndex
    );

    if (manager.current) {
      manager.current.textContent =
        realIndex + 1;
    }

    if (manager.description) {
      manager.description.textContent =
        slide.dataset.description || '';
    }

    requestAnimationFrame(() => {
      manager.description?.classList.remove(
        this.changingClass
      );

      manager.current?.classList.remove(
        this.changingClass
      );
    });
  }

  syncActiveLink(manager, activeSlide) {
    manager.track.querySelectorAll('.product-gallery__link').forEach(link => {
      if (link.closest('.product-gallery__slide') !== activeSlide) {
        link.replaceWith(...link.childNodes);
      }
    });

    if (!activeSlide.dataset.fullSrc || activeSlide.querySelector('.product-gallery__link')) {
      return;
    }

    const image = activeSlide.querySelector('.product-gallery__image');
    if (!image) return;

    const link = document.createElement('a');
    link.className = 'product-gallery__link';
    link.href = activeSlide.dataset.fullSrc;
    image.replaceWith(link);
    link.append(image);
  }

  initMagnificPopup(manager, slides) {
    const items = slides
      .filter(slide => slide.dataset.fullSrc)
      .map(slide => ({
        src: slide.dataset.fullSrc,
        type: 'image',
      }));

    const options = {
          type: 'image',
          showCloseBtn: true,
          mainClass:
            'archon-product-showcase-images-popup product-gallery-popup',
          tClose: 'Close (Esc)',
          tLoading: 'Loading…',
    
          gallery: {
            enabled: true,
            tPrev: 'Previous',
            tNext: 'Next',
            tCounter:
              '<span class="mfp-counter">%curr% of %total%</span>',
          },
    
          zoom: {
            enabled: false,
            duration: 300,
            opener(element) {
              return element.find('img');
            },
          },
    
          callbacks: {
            open() {
              const mfp = jQuery.magnificPopup.instance;
              const proto = jQuery.magnificPopup.proto;

              mfp.next = proto.next;
              mfp.prev = proto.prev;
              mfp.container.removeClass('mfp-first mfp-last');
            },
          },
        };

    jQuery(manager.track).on(
      'click.product-gallery-popup',
      '.product-gallery__link',
      event => {
        event.preventDefault();
        event.stopPropagation();

        const slide = event.currentTarget.closest(
          '.product-gallery__slide'
        );
        const index = Number(slide?.dataset.realIndex);

        jQuery.magnificPopup.open(
          {
            ...options,
            items,
          },
          Number.isInteger(index) ? index : 0
        );
      }
    );
  }
}

new ProductGallery();
